import React from 'react';
import EmptyState from '../../../components/shared/EmptyState';
import LabelAvatarStack from '../../../components/shared/LabelAvatarStack';

function RingtoneSongTable({ 
  songs, 
  currentPage = 1, 
  totalSongs = 0, 
  songsPerPage = 20, 
  onPageChange, 
  onSelectSong,
  onScroll,
  t
}) {
  const totalPages = Math.ceil(totalSongs / songsPerPage) || 1;
  
  // Generate page numbers
  const pageNumbers = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  } else {
    for (let i = 1; i <= 6; i++) {
      pageNumbers.push(i);
    }
    pageNumbers.push('ellipsis');
    pageNumbers.push(totalPages);
  }

  const startIdx = totalSongs === 0 ? 0 : (currentPage - 1) * songsPerPage + 1;
  const endIdx = Math.min(currentPage * songsPerPage, totalSongs);

  return (
    <div className="song-table-container" onScroll={onScroll} style={{ maxHeight: '550px', overflowY: 'auto' }}>
      {/* Table Headers */}
      <div className="song-table-grid song-table-header">
        <div>{t('albumDetails.songsColSongName') || 'Song Name'}</div>
        <div>{t('albumDetails.songsColStatus') || 'Status'}</div>
        <div>{t('albumDetails.rowArtist') || 'Artist'}</div>
        <div>Distributor</div>
        <div>{t('albumDetails.songsColLyricsMusic') || 'Lyrics & Music'}</div>
        <div className="center-header">{t('albumDetails.songsColLabels') || 'Labels'}</div>
        <div className="center-header">{t('albumDetails.songsColOwnership') || 'Ownership'}</div>
        <div>{t('albumDetails.songsColNotesCases') || 'Notes / Cases'}</div>
        <div>{t('albumDetails.songsColConflicts') || 'Conflicts'}</div>
      </div>

      {/* Table Body Rows */}
      <div className="song-table-body">
        {songs.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', padding: '40px 0' }}>
            <EmptyState
              title="No related songs found"
              subtitle="There are no active songs associated with this ringtone operator yet."
              hasSearch={false}
            />
          </div>
        ) : (
          songs.map((song, index) => {
            const isFirstRow = index === 0;
            return (
              <div
                key={song.id || index}
                className={`song-table-grid song-row ${isFirstRow ? 'highlighted-row' : ''}`}
                onClick={() => onSelectSong && onSelectSong(song)}
              >
                {/* Song Name */}
                <div className="cell-song-name">
                  <div className="main-text">{song.name}</div>
                </div>

                {/* Status */}
                <div>
                  <span className="status-pill active">
                    <span className="status-dot"></span>
                    Active
                  </span>
                </div>

                {/* Artist */}
                <div>
                  <div className="main-text">{(song.artist || '').split(',').map(name => name.trim()).filter(Boolean).map(name => name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')).join(' / ')}</div>
                </div>

                {/* Distributor */}
                <div>
                  <div className="main-text">{song.distributor}</div>
                </div>

                {/* Lyrics & Music */}
                <div>
                  <div className="main-text lyrics-text">{song.lyrics || '—'}</div>
                  <div className="sub-text music-text">{song.music || '—'}</div>
                </div>

                {/* Record Labels */}
                <div className="cell-labels center-text">
                  <LabelAvatarStack labels={song.labels || song.recordLabels} />
                </div>

                {/* Ownership */}
                <div className="cell-ownership center-text">
                  {song.ownership !== undefined ? song.ownership : (song.ownershipPercentage !== undefined ? song.ownershipPercentage : 0)}%
                </div>

                {/* Notes / Cases */}
                <div className="cell-notes">
                  {song.notes || '—'}
                </div>

                {/* Conflicts */}
                <div className="cell-conflicts">
                  <span className={`conflicts-badge ${song.conflictCount > 0 || (song.conflict && song.conflict !== 'No' && song.conflict !== '0') ? 'conflict' : 'no-conflict'}`}>
                    {song.conflicts || song.conflict || 'No'}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Footer */}
      <div className="table-pagination-footer">
        <div className="pagination-info">
          {startIdx}-{endIdx} of {totalSongs}
        </div>
        
        <div className="pagination-controls">
          {/* Double Left Arrow */}
          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === 1}
            onClick={() => onPageChange && onPageChange(1)}
            aria-label="First Page"
          >
            &lt;&lt;
          </button>
          
          {/* Single Left Arrow */}
          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === 1}
            onClick={() => onPageChange && onPageChange(currentPage - 1)}
            aria-label="Previous Page"
          >
            &lt;
          </button>

          {/* Page Numbers */}
          {pageNumbers.map((num, i) => {
            if (num === 'ellipsis') {
              return <span key={`ellipsis-${i}`} className="pagination-ellipsis">...</span>;
            }
            return (
              <button
                key={`page-${num}`}
                className={`pagination-btn num-btn ${currentPage === num ? 'active' : ''}`}
                onClick={() => onPageChange && onPageChange(num)}
              >
                {num}
              </button>
            );
          })}

          {/* Single Right Arrow */}
          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange && onPageChange(currentPage + 1)}
            aria-label="Next Page"
          >
            &gt;
          </button>

          {/* Double Right Arrow */}
          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange && onPageChange(totalPages)}
            aria-label="Last Page"
          >
            &gt;&gt;
          </button>
        </div>
      </div>
    </div>
  );
}

export default RingtoneSongTable;
